// components/calendar/CalendarHeader.js
// Header con navigazione, toggle vista, filtro utente e pulsanti azione

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, Plus, Link2, Link2Off, RefreshCw, Loader2 } from 'lucide-react';
import { format, startOfWeek, endOfWeek, getWeek } from 'date-fns';
import { it } from 'date-fns/locale';

const CalendarHeader = ({
  currentDate,
  currentMonth, // Kept for backwards compatibility
  viewMode = 'month', // 'day' | 'week' | 'month'
  onViewModeChange,
  onPrev,
  onNext,
  onPrevMonth, // Backwards compatibility
  onNextMonth, // Backwards compatibility
  selectedCalendarUser,
  setSelectedCalendarUser,
  visibleUsers,
  googleConnected,
  onConnectGoogle,
  onDisconnectGoogle,
  onSyncAllToGoogle,
  syncingAll,
  canCreateAppointment,
  onNewAppointment,
  isAdmin,
  isProjectManager
}) => {
  // Use currentDate if provided, otherwise fall back to currentMonth
  const activeDate = currentDate || currentMonth || new Date();
  
  // Get display text based on view mode
  const getNavigationText = () => {
    switch (viewMode) {
      case 'day':
        return format(activeDate, 'd MMMM yyyy', { locale: it });
      case 'week':
        const weekStart = startOfWeek(activeDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(activeDate, { weekStartsOn: 1 });
        const weekNum = getWeek(activeDate, { weekStartsOn: 1 });
        return `Settimana ${weekNum}: ${format(weekStart, 'd', { locale: it })} - ${format(weekEnd, 'd MMMM', { locale: it })}`;
      case 'month':
      default:
        return format(activeDate, 'MMMM yyyy', { locale: it });
    }
  };

  // Handle navigation - use new props if available, fall back to old ones
  const handlePrev = () => {
    if (onPrev) onPrev();
    else if (onPrevMonth) onPrevMonth();
  };

  const handleNext = () => {
    if (onNext) onNext();
    else if (onNextMonth) onNextMonth();
  };

  return (
    <Card className="card-abitalo">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Navigation and View Toggle */}
          <div className="flex items-center gap-4">
            {/* Prev/Next Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrev}
                className="h-9 w-9"
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNext}
                className="h-9 w-9"
                data-testid="next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Current Period Display */}
            <h2 className="text-lg font-semibold text-[#3C3C3C] min-w-[220px]">
              {getNavigationText()}
            </h2>

            {/* View Mode Toggle */}
            {onViewModeChange && (
              <div className="flex items-center bg-[#F0F0F0] rounded-lg p-1 gap-1">
                <button
                  onClick={() => onViewModeChange('day')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                    viewMode === 'day' 
                      ? 'bg-white text-[#FF7819] border border-[#FF7819] shadow-sm' 
                      : 'text-[#5C5C5C] hover:bg-white/60 hover:text-[#3C3C3C]'
                  }`}
                  data-testid="view-day-btn"
                >
                  Giorno
                </button>
                <button
                  onClick={() => onViewModeChange('week')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                    viewMode === 'week' 
                      ? 'bg-white text-[#FF7819] border border-[#FF7819] shadow-sm' 
                      : 'text-[#5C5C5C] hover:bg-white/60 hover:text-[#3C3C3C]'
                  }`}
                  data-testid="view-week-btn"
                >
                  Settimana
                </button>
                <button
                  onClick={() => onViewModeChange('month')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                    viewMode === 'month' 
                      ? 'bg-white text-[#FF7819] border border-[#FF7819] shadow-sm' 
                      : 'text-[#5C5C5C] hover:bg-white/60 hover:text-[#3C3C3C]'
                  }`}
                  data-testid="view-month-btn"
                >
                  Mese
                </button>
              </div>
            )}
          </div>

          {/* Filters and Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Calendar User Filter */}
            {visibleUsers?.length > 1 && (
              <Select value={selectedCalendarUser} onValueChange={setSelectedCalendarUser}>
                <SelectTrigger className="w-[180px]" data-testid="calendar-user-filter">
                  <SelectValue placeholder="Tutti i calendari" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i calendari</SelectItem>
                  {visibleUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Google Calendar Integration */}
            {(isAdmin || isProjectManager) && (
              <>
                {googleConnected ? (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSyncAllToGoogle}
                      disabled={syncingAll}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      data-testid="sync-all-google-btn"
                    >
                      {syncingAll ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Sync Google
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onDisconnectGoogle}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                      title="Disconnetti Google Calendar"
                      data-testid="disconnect-google-btn"
                    >
                      <Link2Off className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Disconnetti</span>
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onConnectGoogle}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    data-testid="connect-google-btn"
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    Collega Google
                  </Button>
                )}
              </>
            )}

            {/* New Appointment Button */}
            {canCreateAppointment && (
              <Button 
                className="btn-primary"
                onClick={onNewAppointment}
                data-testid="new-appointment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarHeader;
